import { APP_CONFIG } from '../config/app';
import { QualityPreset } from '../config/quality';

export type WeatherType = 'sunny' | 'rain' | 'snow';
export type LanguageType = 'vi' | 'en' | 'zh-CN';

export interface AppState {
  sceneId: string;
  timeOfDay: number; // 0..5 (0: dawn, 1: morning, 2: noon, 3: golden hour, 4: dusk, 5: night)
  timeScale: number; // 0 or 1
  isPaused: boolean;
  weather: WeatherType;
  quality: QualityPreset;
  optimizePerformance: boolean;
  tiltShift: boolean;
  pixelArt: boolean;
  inkLines: boolean;
  bloom: boolean;
  language: LanguageType;
  reduceMotion: boolean;
  hudVisible: boolean;
  cameraView: number; // 1..6
  chillMode: boolean; // auto-tour
  // Audio state
  audioEnabled: boolean;
  audioPlaying: boolean;
  audioVolume: number; // 0..100
  audioMuted: boolean;
  audioLoop: boolean;
  audioRepeatOne: boolean;
  currentTrackTitle: string;
  currentTrackMood: string;
  sleepTimerMinutes: number | null; // null or remaining minutes
}

const DEFAULT_STATE: AppState = {
  sceneId: APP_CONFIG.defaultScene,
  timeOfDay: 1, // morning
  timeScale: 1,
  isPaused: false,
  weather: 'sunny',
  quality: 'auto',
  optimizePerformance: false,
  tiltShift: false,
  pixelArt: false,
  inkLines: false,
  bloom: true,
  language: 'vi',
  reduceMotion: false,
  hudVisible: true,
  cameraView: 1,
  chillMode: false,
  audioEnabled: false,
  audioPlaying: false,
  audioVolume: 65,
  audioMuted: false,
  audioLoop: true,
  audioRepeatOne: false,
  currentTrackTitle: 'Khúc Mộng Vân Nam (Sáo & Cổ Tranh)',
  currentTrackMood: 'Ngọc Long · Bình Minh',
  sleepTimerMinutes: null,
};

type Listener = (state: AppState) => void;

class Store {
  private state: AppState;
  private listeners: Set<Listener> = new Set();

  constructor() {
    this.state = this.loadPersistedState();
    // Auto detect user language if not explicitly stored
    if (!localStorage.getItem(APP_CONFIG.storageKey)) {
      const navLang = navigator.language.toLowerCase();
      if (navLang.startsWith('zh')) {
        this.state.language = 'zh-CN';
      } else if (navLang.startsWith('en')) {
        this.state.language = 'en';
      } else {
        this.state.language = 'vi';
      }
    }
  }

  public getState(): AppState {
    return this.state;
  }

  public setState(partial: Partial<AppState>): void {
    this.state = { ...this.state, ...partial };
    this.persistState();
    this.notify();
  }

  public subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) {
      try {
        listener(this.state);
      } catch (err) {
        console.error('Store listener error:', err);
      }
    }
  }

  private loadPersistedState(): AppState {
    try {
      const raw = localStorage.getItem(APP_CONFIG.storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          ...DEFAULT_STATE,
          ...parsed,
          // Always start unpaused and audio stopped until user interaction
          isPaused: false,
          audioPlaying: false,
          chillMode: false,
        };
      }
    } catch (e) {
      console.warn('Failed to load settings from localStorage:', e);
    }
    return { ...DEFAULT_STATE };
  }

  public persistState(): void {
    try {
      const toSave = {
        sceneId: this.state.sceneId,
        timeOfDay: this.state.timeOfDay,
        weather: this.state.weather,
        quality: this.state.quality,
        optimizePerformance: this.state.optimizePerformance,
        tiltShift: this.state.tiltShift,
        pixelArt: this.state.pixelArt,
        inkLines: this.state.inkLines,
        bloom: this.state.bloom,
        language: this.state.language,
        reduceMotion: this.state.reduceMotion,
        audioVolume: this.state.audioVolume,
        audioMuted: this.state.audioMuted,
        audioLoop: this.state.audioLoop,
        audioRepeatOne: this.state.audioRepeatOne,
      };
      localStorage.setItem(APP_CONFIG.storageKey, JSON.stringify(toSave));
    } catch (e) {
      console.warn('Failed to persist settings:', e);
    }
  }

  public resetSettings(): void {
    try {
      localStorage.removeItem(APP_CONFIG.storageKey);
    } catch {
      // ignore
    }
    this.setState({ ...DEFAULT_STATE });
  }
}

export const store = new Store();
